/**
 * „Bitte Seite neu laden“ auf Zuruf — was das Gerät daraus macht. (11.10.2026, Ticket 2669)
 *
 * Martin, 26.09., auf der Autobahn: Nach einem Update erscheint „Neue Version“, und den Knopf erreicht er
 * beim Fahren nicht. Das Modell ruft das Werkzeug `app_neu_laden` (tools/app_neu_laden.ts); der Server prüft
 * Kanal, Fremdtext und Abstand. Hier steht, WANN die Seite wirklich neu lädt.
 *
 * Der Ablauf:
 *   1. dispatchToolCard (app.ts, nur live) merkt den Auftrag vor (`merkeNeuLaden`). Aus dem Verlauf lädt nichts.
 *   2. Geladen wird erst, wenn Ruhe ist — und zwar RUHE_TAKTE Takte hintereinander: kein Anruf, keine
 *      Sprechtaste, keine Aufnahme, keine Wiedergabe, keine laufende Antwort, kein ungesendeter Text. So
 *      bleibt Vesperas gesprochene Bestätigung ganz, und eine kurze Lücke zwischen Text und erstem Ton
 *      reicht nicht für ein Neuladen mitten im Satz.
 *   3. Läuft nach LAENGSTENS_MS immer noch etwas, steht eine Zeile da, WARUM nicht (und „sag es noch einmal“).
 *      Ein Anruf und ein ungesendeter Text warten nicht: beide enden nicht von selbst.
 *   4. Das Neuladen selbst ist der Weg des Knopfs „Neue Version“ (workerWechsel.aktivierenUndNeuLaden): ein
 *      wartender Service-Worker übernimmt zuerst, sonst käme die neue Seite mit dem alten Worker zurück.
 *
 * ⚠️ EIN Besitzer: nur diese Datei hält den vorgemerkten Auftrag. Ein neuer Zug (eroeffneZug) und ein neuer
 *    Druck auf die Sprechtaste verwerfen ihn — er wollte die Seite neu laden, nicht seine nächste Aufnahme
 *    abreißen. Solange er wartet, öffnet sich nach der Antwort kein Mikro (`neuLadenWartet`).
 * ⚠️ Zweite Bremse am Gerät: höchstens ein Neuladen je 15 Sekunden je Sitzung, im sessionStorage — der überlebt
 *    das Neuladen. Der Server bremst das Modell schon; diese hier hält auch dann, wenn ein Auftrag aus
 *    irgendeinem Grund zweimal ankäme. Eine Schleife „laden, Auftrag, laden“ ist damit ausgeschlossen.
 * ⚠️ Das FENSTER zwischen „freigegeben“ und „wirklich geladen“ (Erreichbarkeits-Prüfung bis 4 s, wartender Worker
 *    bis 3,5 s): Fängt er darin an zu sprechen oder startet eine Aufnahme, wird NICHT geladen. Der Weg des Knopfs
 *    fragt dafür unmittelbar vor `location.reload()` noch einmal (`neuLadenDarfJetzt`), und ein neuer Zug oder ein
 *    Druck auf die Sprechtaste bricht das Laden im Fenster ab (`zugOhneNeuLaden`).
 * ⚠️ Ein Auftrag verfällt nach 30 Sekunden — auch wenn die Takte stillstanden (Seite im Hintergrund): wer nach
 *    Minuten zurückkommt, bekommt kein Neuladen aus einer Bitte von vorhin.
 */
import { NEU_LADEN_ABSTAND_MS, NEU_LADEN_WERKZEUG } from "../../shared/app-neu-laden.js";
import { auspacken } from "./schritte.js";

/** Das Werkzeug, mit dem das Modell das Neuladen anfordert — der Name steht in shared/. */
export { NEU_LADEN_WERKZEUG };

/** Alle 400 ms nachsehen, ob Ruhe ist. */
export const NEU_LADEN_TAKT_MS = 400;
/** So viele ruhige Takte hintereinander (1,2 s), bevor geladen wird. */
export const NEU_LADEN_RUHE_TAKTE = 3;
/** Nach so langer Unruhe wird nicht mehr gewartet — und so alt darf ein Auftrag höchstens werden. */
export const NEU_LADEN_LAENGSTENS_MS = 30_000;
/** So lange nach der Freigabe gilt der letzte Blick auf die Lage noch (Prüfung + Worker brauchen bis 4 s). */
export const NEU_LADEN_FENSTER_MS = 10_000;
/** Wann zuletzt auf Zuruf neu geladen wurde (sessionStorage überlebt das Neuladen, ein Kaltstart leert ihn). */
export const NEU_LADEN_MERKER = "pawbot:neuLadenAm";

/** Nur ein gelungenes Ergebnis, das ausdrücklich `neuLaden: true` trägt, ist ein Auftrag. */
export function neuLadenAuftrag(tool: string, isError: boolean, result: unknown): boolean {
  if (tool !== NEU_LADEN_WERKZEUG || isError) return false;
  const r = auspacken(result);
  return r.ok === true && r.neuLaden === true;
}

/** Was gerade läuft — alles, was ein Neuladen abreißen würde. */
export interface NeuLadenLage {
  /** Läuft ein Anruf? */
  anruf: boolean;
  /** Nimmt die Sprechtaste auf oder sendet sie? */
  sprechtaste: boolean;
  /** Läuft eine Tages- oder Seitenaufnahme? */
  aufnahme: boolean;
  /** Hält die Seite ein Mikrofon für etwas anderes (Diktat der Tiefen Frage)? Das warme Mikro der Sprechtaste zählt nicht. */
  mikro: boolean;
  /** Klingt gerade etwas (Vorlesen, Spur, Musik)? */
  ton: boolean;
  /** Läuft eine Antwort (Zug offen)? */
  antwort: boolean;
  /** Steht ungesendeter Text im Eingabefeld? */
  entwurf: boolean;
}

type NeuLadenHindernis = keyof NeuLadenLage;

/** Der Grund in Worten, so steht er auf der Zeile — „erst nach …“. */
export const HINDERNIS_SATZ: Readonly<Record<NeuLadenHindernis, string>> = {
  anruf: "erst nach dem Auflegen",
  sprechtaste: "erst nach der Aufnahme mit der Sprechtaste",
  aufnahme: "erst nach dem Ende der Aufnahme",
  mikro: "erst wenn das Mikrofon wieder frei ist (Diktat)",
  ton: "erst nach der Wiedergabe",
  antwort: "erst nach Vesperas Antwort",
  entwurf: "erst wenn der Text im Eingabefeld gesendet oder gelöscht ist",
};

/** Hindernisse, die nicht von selbst enden — dafür lohnt kein Warten. */
const WARTET_NICHT: ReadonlySet<NeuLadenHindernis> = new Set<NeuLadenHindernis>(["anruf", "entwurf"]);

/**
 * Klingt gerade etwas — oder steht eine Stimme an? (11.10.2026, Prüfbefund zu Ticket 2669)
 *
 * Der Ton-Leitstand (`aktuellerTon`) kennt nur die Abspielleiste (Spur, Vorlesen einer Blase, Musik). Vesperas
 * Antwortstimme läuft über eigene Warteschlangen (`TtsQueue`), die sich dort nie anmelden. Im getippten Chat
 * schließt der Zug, BEVOR das Vorlesen beginnt: nach 1,2 s „Ruhe“ hätte die Seite mitten im Satz neu geladen.
 * Darum zählt eine Warteschlange, solange sie nicht fertig UND still ist — auch eine, die noch auf ihren Text
 * wartet oder ihr erstes Stück vertont. (Die vorhandene Quelle, keine zweite Liste: `activeTtsQueues`, app.ts.)
 *
 * ⚠️ „fertig und still“ (`isSilentIdle`) allein genügt NICHT: Eine Schlange, die ihren Text schon abgegeben hat
 *    (`finished`), aber noch vertont, spielt nichts und ist doch nicht fertig — genau die Lücke vor dem ersten Ton.
 *    Darum zusätzlich `isAudioActive` (spielt oder hat Stücke in der Warteschlange).
 */
export function klingtEtwas(
  ton: object | null,
  schlangen: Iterable<{ isSilentIdle(): boolean; isAudioActive(): boolean }>,
): boolean {
  if (ton !== null) return true;
  for (const q of schlangen) if (!q.isSilentIdle() || q.isAudioActive()) return true;
  return false;
}

/** Warum (noch) nicht geladen wird — `null` heißt: Ruhe. Die Reihenfolge ist die der Auskunft. */
export function neuLadenHindernis(l: NeuLadenLage): NeuLadenHindernis | null {
  if (l.anruf) return "anruf";
  if (l.entwurf) return "entwurf";
  if (l.sprechtaste) return "sprechtaste";
  if (l.aufnahme) return "aufnahme";
  if (l.mikro) return "mikro";
  if (l.ton) return "ton";
  if (l.antwort) return "antwort";
  return null;
}

/** Was das Gerät dazu liefert. */
export interface NeuLadenUmgebung {
  lage(): NeuLadenLage;
  /** Der Weg des Knopfs „Neue Version“ (workerWechsel.aktivierenUndNeuLaden). */
  neuLaden(): void;
  /** Eine sichtbare Zeile für ihn. */
  hinweis(satz: string): void;
  /** Eine Zeile ins Protokoll — nur Wörter und Zahlen. */
  melde(zeile: string): void;
}

interface Lauf {
  zeit: ReturnType<typeof setTimeout> | null;
  u: NeuLadenUmgebung;
}
let lauf: Lauf | null = null;

/** Das Fenster zwischen Freigabe und echtem Laden: der Weg des Knopfs fragt darin unmittelbar vor `location.reload()`. */
interface Ausfuehrung {
  u: NeuLadenUmgebung;
  bis: number;
  abgebrochen: boolean;
}
let ausfuehrung: Ausfuehrung | null = null;

function imFenster(): Ausfuehrung | null {
  if (ausfuehrung && Date.now() >= ausfuehrung.bis) ausfuehrung = null;
  return ausfuehrung;
}

/** Wartet ein Auftrag — oder lädt er gerade? Dann öffnet das Vorlese-Ende kein Mikro. */
export function neuLadenWartet(): boolean {
  const a = imFenster();
  return lauf !== null || (a !== null && !a.abgebrochen);
}

/** Ein neuer Zug oder ein neuer Druck: kein Auftrag von vorhin — auch nicht einer, der schon lädt. */
export function zugOhneNeuLaden(): void {
  const a = imFenster();
  if (a && !a.abgebrochen) {
    a.abgebrochen = true;
    a.u.melde("[neu-laden] verworfen im-fenster");
  }
  if (!lauf) return;
  if (lauf.zeit !== null) clearTimeout(lauf.zeit);
  lauf.u.melde("[neu-laden] verworfen");
  lauf = null;
}

/**
 * Unmittelbar vor `location.reload()`: darf jetzt wirklich geladen werden? (Prüfbefund zu Ticket 2669)
 *
 * Zwischen Freigabe und Laden liegen bis zu 4 s (Erreichbarkeit) bzw. 3,5 s (Worker). Beginnt darin eine
 * Aufnahme oder eine Stimme, oder hat ein neuer Zug / Druck den Auftrag verworfen, lädt nichts. Ohne einen Auftrag
 * auf Zuruf (Knopf „Neue Version“, stilles Nachladen) ist die Antwort immer ja — diese Wege behalten ihre eigenen Regeln.
 *
 * ⚠️ „Ohne einen Auftrag“ ist HIER nicht von selbst wahr: Knopf, stilles Nachladen und der Zuruf teilen sich dieselbe Kette
 *    (worker-wechsel.ts) und damit diese Frage. War ein Zuruf im Fenster abgebrochen, sagte sie bis zu `NEU_LADEN_FENSTER_MS`
 *    (10 s) auch dem Knopf nein (Integrationsprüfung 11.10.2026). Darum nehmen beide ihren Weg über `neuLadenOhneZuruf()`.
 */
export function neuLadenDarfJetzt(): boolean {
  const a = imFenster();
  if (!a) return true;
  if (a.abgebrochen) return false;
  const h = neuLadenHindernis(a.u.lage());
  if (!h) return true;
  a.abgebrochen = true;
  a.u.melde(`[neu-laden] abgebrochen im-fenster grund=${h}`);
  a.u.hinweis(`Neu laden: ${HINDERNIS_SATZ[h]}. Sag es danach noch einmal.`);
  return false;
}

/**
 * Der Knopf „Neue Version“ und das stille Nachladen starten ihren EIGENEN Weg: ein Auftrag auf Zuruf, der im Fenster noch
 * offen oder abgebrochen ist, gilt für sie nicht. Der Knopf ist ein ausdrücklicher Wunsch, das stille Nachladen hat seine
 * eigenen Regeln (lib/stilles-update.ts). Gerufen von app.ts unmittelbar vor `workerWechsel.aktivierenUndNeuLaden()`.
 */
export function neuLadenOhneZuruf(): void {
  ausfuehrung = null;
  // Ein Zuruf, der noch auf Ruhe WARTET (`lauf`), wäre sonst nach dem Knopf wieder da: er würde im nächsten Takt ein neues Fenster
  // setzen (`ausfuehrung`) und dem Knopf bei seiner letzten Frage doch noch nein sagen. Der Knopf lädt ohnehin — der Auftrag ist erledigt.
  if (lauf) {
    if (lauf.zeit !== null) clearTimeout(lauf.zeit);
    lauf.u.melde("[neu-laden] verworfen knopf");
    lauf = null;
  }
}

function zuletztGeladen(): number | null {
  try {
    const v = Number(sessionStorage.getItem(NEU_LADEN_MERKER));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

function stempeln(jetzt: number): void {
  try {
    sessionStorage.setItem(NEU_LADEN_MERKER, String(jetzt));
  } catch {
    /* kein sessionStorage — dann bremst nur der Server */
  }
}

/** dispatchToolCard (live): den Auftrag vormerken — geladen wird, sobald Ruhe ist. Ein zweiter Auftrag hilft nicht. */
export function merkeNeuLaden(u: NeuLadenUmgebung): void {
  if (lauf) return;
  const beginn = Date.now();
  const zuletzt = zuletztGeladen();
  if (zuletzt !== null && beginn - zuletzt >= 0 && beginn - zuletzt < NEU_LADEN_ABSTAND_MS) {
    const rest = Math.ceil((NEU_LADEN_ABSTAND_MS - (beginn - zuletzt)) / 1000);
    u.melde("[neu-laden] abgelehnt zu-bald");
    // Ehrlich: ob die Seite wirklich geladen hat, weiß dieser Satz nicht (Server nicht erreichbar, abgebrochen …).
    u.hinweis(`Das Neuladen wurde gerade erst angestoßen. In ${rest} Sekunden geht es wieder.`);
    return;
  }
  const mein: Lauf = { zeit: null, u };
  lauf = mein;
  let ruhig = 0;
  let letztes: NeuLadenHindernis | null = null;

  const gib = (grund: NeuLadenHindernis): void => {
    lauf = null;
    u.melde(`[neu-laden] abgebrochen grund=${grund}`);
    u.hinweis(`Neu laden: ${HINDERNIS_SATZ[grund]}. Sag es danach noch einmal.`);
  };

  const takt = (): void => {
    if (lauf !== mein) return;
    mein.zeit = null;
    const h = neuLadenHindernis(u.lage());
    // Zu alt: die Takte standen still (Seite im Hintergrund, Telefon gesperrt) — kein Neuladen aus einer Bitte von vorhin.
    if (Date.now() - beginn >= NEU_LADEN_LAENGSTENS_MS) {
      if (h) gib(h);
      else {
        lauf = null;
        u.melde("[neu-laden] verfallen");
        u.hinweis("Neu laden: zu lange her. Sag es noch einmal.");
      }
      return;
    }
    if (h) {
      ruhig = 0;
      letztes = h;
      if (WARTET_NICHT.has(h)) {
        gib(h);
        return;
      }
    } else {
      ruhig += 1;
      if (ruhig >= NEU_LADEN_RUHE_TAKTE) {
        lauf = null;
        stempeln(Date.now());
        ausfuehrung = { u, bis: Date.now() + NEU_LADEN_FENSTER_MS, abgebrochen: false };
        u.melde(`[neu-laden] los nachMs=${Date.now() - beginn}${letztes ? ` zuletzt=${letztes}` : ""}`);
        u.neuLaden();
        return;
      }
    }
    mein.zeit = setTimeout(takt, NEU_LADEN_TAKT_MS);
  };

  mein.zeit = setTimeout(takt, NEU_LADEN_TAKT_MS);
}
